import PropTypes from "prop-types";
import { Navigate } from "react-router-dom";
import ProcessingLoader from "../components/ProcessingLoader";
import { checkRoutePermissions } from "../helper/permissionService";
import { permissionsFn } from "../helper/com-helper";
import { useAuth } from "../provider/AuthContext";

// Token verification, refresh, idle logout, and INVALID_TOKEN handling all
// live in AuthProvider now (mounted once) instead of here — this component
// is recreated fresh on every route change (each distinct path is its own
// <Route element={<PrivateRoute .../>}>), so keeping that logic here meant
// re-running a full async /token/verify round-trip on every navigation,
// rendering neither the destination nor a stable error state until it
// resolved. This is now a synchronous gate over already-known, stable state.
const PrivateRoute = ({ children, path }) => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const permissions = permissionsFn(user);

  if (authLoading) {
    return <ProcessingLoader />;
  }

  const validRoute = checkRoutePermissions(path, permissions);

  if (validRoute && isAuthenticated) {
    return children;
  }

  // Not authenticated at all (e.g. just logged out) → send to login, not the
  // generic error page. checkRoutePermissions always returns false once
  // `permissions` is empty (post-logout), which used to make this branch on
  // `validRoute` instead — sending a freshly-logged-out user to /error
  // first, since no empty permission set satisfies any real route's
  // requirement, before whatever else in the logout flow navigated them the
  // rest of the way to login. Authenticated but lacking permission for this
  // specific route is the only case that should show /error.
  return <Navigate to={isAuthenticated ? "/error" : "/"} />;
};

PrivateRoute.propTypes = {
  children: PropTypes.node,
  path: PropTypes.string,
};

export default PrivateRoute;
